// src/routes/index.tsx
import React, { Suspense, lazy, useEffect, useMemo } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../lib/store';
import { getAllowedRoutes, getNavigationItems } from '../components/Layout/Sider/navigation';
import menuConfig from '../config/menuConfig.json';
import PublicLayout from '../components/Layout/PublicLayout';
import AuthGuard from '../components/Layout/AuthGuard';
import AuthedLayout from '../components/Layout/AuthedLayout';
import { AuthedLayoutProvider } from '../components/Layout/AuthedLayoutContext';
import { NotFound } from '../components/Layout/NotFound';
import { LoadingFallback } from '../components/shared/LoadingFallback';
import { useTranslation } from 'react-i18next';
import Data from '../pages/settings/Data';
import Config from '../pages/settings/Config';

// --- Lazy Load Components ---
const Home = lazy(() => import('../pages/Home'));
const Login = lazy(() => import('../pages/auth/Login'));
const ResetPassword = lazy(() => import('../pages/auth/ResetPassword'));
const Signup = lazy(() => import('../pages/auth/Signup'));
const WebRegister = lazy(() => import('../pages/auth/WebRegister'));
const PublicSubscriptions = lazy(() => import('../pages/public/Subscriptions'));
const QRFormHandler = lazy(() => import('../pages/QRFormHandler'));
const EcomCatalogPage = lazy(() => import('../components/ecom/components/ecom/EcomCatalogPage'));
const TermsOfService = lazy(() => import('../components/pages/Whatsapp/Terms'));
const PrivacyPolicy = lazy(() => import('../components/pages/Whatsapp/Privacy'));
const UserDataDeletion = lazy(() => import('../components/pages/Whatsapp/UserDelete'));
const Profile = lazy(() => import('../pages/core/Profile'));
const UserSetting = lazy(() => import('../pages/core/UserSetting'));
const OldWorkflow = lazy(() => import('../pages/settings/_Workflows'));
const Whatsapp = lazy(() => import('../components/whatsapp/App'));
const DebugCommunication = lazy(() => import('../components/utils/DebugCommunication'));
const Scheduler = lazy(() => import('../components/pages/ProcessEditor/Scheduler'));
const WorkflowBuilder = lazy(() => import('../components/pages/WorkFlow/WorkflowBuilder'));
const WorkflowDetail = lazy(() => import('../components/pages/WorkFlow/WorkflowDetail'));
const WorkflowForm = lazy(() => import('../components/common/WorkflowForm'));
const BulkUpload = lazy(() => import('../components/DynamicViews/BulkUpload'));

// WA Pages
const WaInboxPage = lazy(() => import('../pages/wa/InboxPage'));
const WaTemplatesPage = lazy(() => import('../pages/wa/TemplatesPage'));
const WaAnalyticsPage = lazy(() => import('../pages/wa/AnalyticsPage'));
const WaContactsPage = lazy(() => import('../pages/wa/ContactsPage'));
const WaSettingsPage = lazy(() => import('../pages/wa/SettingsPage'));
const WaSequencesPage = lazy(() => import('../pages/wa/SequencesPage'));
const WaLibraryPage = lazy(() => import('../pages/wa/LibraryPage'));
const WaQuickRepliesPage = lazy(() => import('../pages/wa/QuickRepliesPage'));
const WaEcomAdminPage = lazy(() => import('../pages/wa/EcomAdminPage'));
const WaDripCampaignBuilder = lazy(() => import('../pages/wa/DripCampaignBuilder'));
const WaHelpPage = lazy(() => import('../pages/wa/HelpPage'));
const WaVariablesPage = lazy(() => import('../pages/wa/VariablesPage'));
const WaLayout = lazy(() => import('../components/wa/WaLayout'));

const componentMap: Record<string, React.ComponentType> = {
  'dashboard': lazy(() => import('../pages/Dashboard')),
  'user-settings': lazy(() => import('../pages/settings/_UserSettings')),
  'users-view': lazy(() => import('../pages/settings/_UsersView')),
  'crud-view': lazy(() => import('../pages/core/CrudView')),
  'businesses': lazy(() => import('../pages/settings/_Businesses')),
  'settings': lazy(() => import('../pages/admin/Settings')),
  'catalog': lazy(() => import('../pages/admin/Catalog')),
  'shopping': lazy(() => import('../pages/admin/Shopping')),
  'subscriptions': lazy(() => import('../pages/admin/Subscriptions')),
  'notifications': lazy(() => import('../pages/admin/Notifications')),
  'config': lazy(() => import('../pages/settings/Config')),
  'data': lazy(() => import('../pages/settings/Data')),
  'workflows': lazy(() => import('../pages/settings/Workflows')),
  'permissions': lazy(() => import('../pages/settings/Permissions')),
  'ai': lazy(() => import('../pages/settings/Ai')),
  'nlp': lazy(() => import('../pages/settings/Nlp')),
  'tickets': lazy(() => import('../pages/support/Tickets')),
  'tasks': lazy(() => import('../pages/support/Tasks')),
  'activities': lazy(() => import('../pages/support/Activities')),
  'projects': lazy(() => import('../pages/support/Projects')),
  'process': lazy(() => import('../pages/admin/Process')),
  'service-reports': lazy(() => import('../pages/support/ServiceReports')),
  'invoices': lazy(() => import('../pages/support/Invoices')),
  'analytics': lazy(() => import('../pages/support/Analytics')),
  'tracking': lazy(() => import('../pages/fsm/Tracking')),
  'my-tickets': lazy(() => import('../pages/fsm/MyTickets')),
  'teams': lazy(() => import('../pages/workforce/Teams')),
  'leaves': lazy(() => import('../pages/workforce/Leaves')),
  'expenses': lazy(() => import('../pages/workforce/Expenses')),
  'timesheets': lazy(() => import('../pages/workforce/Timesheets')),
  'users': lazy(() => import('../pages/workforce/TeamsUsers')),
  'service-assets': lazy(() => import('../pages/contracts/ServiceAssets')),
  'service-categories': lazy(() => import('../pages/admin/ServiceCategories')),
  'service-contracts': lazy(() => import('../pages/contracts/ServiceContracts')),
  'service-types': lazy(() => import('../pages/admin/ServiceTypes')),
  'service-offerings': lazy(() => import('../pages/admin/ServiceOfferings')),
  'clients': lazy(() => import('../pages/contracts/Clients')),
  'client-contacts': lazy(() => import('../pages/contracts/ClientContacts')),
  'crm-accounts': lazy(() => import('../pages/crm/Accounts')),
  'crm-contacts': lazy(() => import('../pages/crm/Contacts')),
  'crm-leads': lazy(() => import('../pages/crm/Leads')),
  'crm-deals': lazy(() => import('../pages/crm/Deals')),
};

const AppRoutes = React.memo(() => {
  const { user, permissions, setNavigationItems } = useAuthStore(state => ({
    user: state.user,
    permissions: state.permissions,
    setNavigationItems: state.setNavigationItems,
  }));
  const { t, i18n } = useTranslation();
  console.log('>>> [AppRoutes] State from store - User:', user ? user.id : 'undefined');

  useEffect(() => {
    if (permissions && user) {
      console.log('>>> [AppRoutes] Permissions and User exist. Calculating Nav Items.');
      const navItems = getNavigationItems(t, permissions, user);
      setNavigationItems(navItems);
    } else {
      console.log('>>> [AppRoutes] Permissions or User missing in store. Clearing nav items.');
      setNavigationItems([]);
    }
  }, [permissions, user, setNavigationItems, t, i18n.isInitialized]);

  const protectedRoutes = useMemo(() => {
    if (!permissions || !user) {
      console.log('>>> [AppRoutes] Skipping protected route generation (no user/perms).');
      return [];
    }
    console.log('>>> [AppRoutes] Recalculating protected routes...');
    const allowedRoutes = getAllowedRoutes(permissions, user);
    const routes = [];
    menuConfig.root.forEach((route) => {
      if (route.routePath && allowedRoutes.includes(route.routePath) && componentMap[route.key]) {
        routes.push({
          path: route.routePath,
          element: componentMap[route.key],
          key: `root-${route.key}`,
        });
      }
    });
    Object.entries(menuConfig.modules).forEach(([moduleKey, moduleRoutes]) => {
      moduleRoutes.forEach((route) => {
        if (route.routePath && allowedRoutes.includes(route.routePath) && componentMap[route.key]) {
          routes.push({
            path: route.routePath,
            element: componentMap[route.key],
            key: `${moduleKey}-${route.key}`,
          });
        }
      });
    });
    console.log('>>> [AppRoutes] Generated Protected Routes:', routes.map(r => r.path));
    return routes;
  }, [permissions, user]);

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/subscriptions" element={<PublicSubscriptions />} />
          <Route path="/submit" element={<QRFormHandler />} />
          <Route path="/shop" element={<EcomCatalogPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/user-delete" element={<UserDataDeletion />} />
        </Route>
        <Route element={<AuthGuard />}>
          <Route element={<PublicLayout />}>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/reset_password" element={<ResetPassword />} />
            <Route path="/web_register" element={<WebRegister />} />
          </Route>
          <Route element={<AuthedLayoutProvider><AuthedLayout /></AuthedLayoutProvider>}>
            <Route index element={<Navigate to="/dashboard" replace />} />
            {protectedRoutes.map((route) => (
              <Route
                key={route.key}
                path={route.path}
                element={<route.element />}
              />
            ))}
            <Route path="/profile" element={<Profile />} />
            <Route path="/user-settings" element={<UserSetting />} />
            <Route path="/planner" element={<Scheduler />} />
            <Route path="/workflows/new" element={<WorkflowBuilder />} />
            <Route path="/workflows/:id" element={<WorkflowDetail />} />
            <Route path="/workflows/:id/edit" element={<WorkflowBuilder />} />
            <Route path="/auto" element={<OldWorkflow />} />
            <Route path="/wa" element={<Whatsapp />} />
            {/* WA Module Routes - wrapped with WaLayout for PageHeaderProvider */}
            <Route element={<WaLayout />}>
              <Route path="/wa/inbox" element={<WaInboxPage />} />
              <Route path="/wa/templates" element={<WaTemplatesPage />} />
              <Route path="/wa/analytics" element={<WaAnalyticsPage />} />
              <Route path="/wa/contacts" element={<WaContactsPage />} />
              <Route path="/wa/settings" element={<WaSettingsPage />} />
              <Route path="/wa/sequences" element={<WaSequencesPage />} />
              <Route path="/wa/library" element={<WaLibraryPage />} />
              <Route path="/wa/quick-replies" element={<WaQuickRepliesPage />} />
              <Route path="/wa/ecom" element={<WaEcomAdminPage />} />
              <Route path="/wa/drip/:campaignId?" element={<WaDripCampaignBuilder />} />
              <Route path="/sequences/:id" element={<WaDripCampaignBuilder />} />
              <Route path="/wa/help" element={<WaHelpPage />} />
              <Route path="/wa/variables" element={<WaVariablesPage />} />
            </Route>
            <Route path="/upload" element={<BulkUpload supabase={supabase} />} />
            <Route path="/native" element={<DebugCommunication />} />
            <Route path="/settings/config" element={<Config />} /> 
            <Route path="/settings/data" element={<Data />} /> 
            <Route
              path="/workflow_form/:workflowId"
              element={<WorkflowForm />}
            />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
});
export default AppRoutes;